import { PropsWithChildren } from 'react'
import { useCascadingMenu } from '../Root'
import { useHandleItemKeyPress } from '../useHandleItemKeypress'
import * as styles from './styles'

type FooterProps = {
  className?: string
  onSelect?: () => void
}

export const InteractiveFooter: React.FC<PropsWithChildren<FooterProps>> = ({
  children,
  className,
  onSelect,
}) => {
  const {
    onActiveDescendentChange,
    setFocusedItemIndex,
    interactiveFooter,
    searchResults,
  } = useCascadingMenu()

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()

    if (!onSelect) return

    setFocusedItemIndex(searchResults.length)
    onSelect()
  }

  const { handleKeyPress, itemRef } = useHandleItemKeyPress<HTMLDivElement>({
    disabledKeys: ['ArrowDown', 'ArrowRight'],
    focusDisabled: !onSelect,
    hasChildren: false,
    index: searchResults.length,
    onDescendMenu: null,
    onSelect: onSelect ?? null,
  })

  if (!interactiveFooter) {
    console.error(
      'CascadingMenu.InteractiveFooter must be used with the interactiveFooter prop on CascadingMenu.Root set to true'
    )
    return null
  }

  return (
    <div
      className={className}
      id={styles.FOOTER_ID}
      role="button"
      ref={itemRef}
      tabIndex={-1}
      css={[styles.footer, styles.footerHoverStyles]}
      onClick={handleClick}
      onFocus={() => onActiveDescendentChange?.(styles.FOOTER_ID)}
      onKeyDown={handleKeyPress}
    >
      {children}
    </div>
  )
}
InteractiveFooter.displayName = 'Lapidary: CascadingMenu.InteractiveFooter'

export const InertFooter: React.FC<
  PropsWithChildren<Omit<FooterProps, 'onSelect'>>
> = ({ className, children }) => {
  const { interactiveFooter } = useCascadingMenu()

  if (interactiveFooter) {
    console.error(
      'Use CascadingMenu.InteractiveFooter to enable keyboard and mouse events in the footer'
    )
  }

  return (
    <div tabIndex={-1} css={styles.footer} className={className}>
      {children}
    </div>
  )
}
InertFooter.displayName = 'Lapidary: CascadingMenu.InertFooter'
